import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, InstagramLogo } from "@phosphor-icons/react";
import { LOGO, CONTACT } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/community", label: "Community" },
  { to: "/partners", label: "Partners & Impact" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-cream text-charcoal selection:bg-teal selection:text-cream">
      {/* Announcement bar */}
      <div className="bg-charcoal text-cream/90 text-[11px] tracking-editorial uppercase py-2 text-center font-sans" data-testid="announcement-bar">
        <span>A women-led wellness community · Boston · Flowing with purpose</span>
      </div>

      {/* Nav */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? "glass shadow-[0_1px_0_rgba(43,43,43,0.08)]" : "bg-transparent"}`}
        data-testid="primary-nav"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
            <img src={LOGO} alt="Flowtastics" className="h-10 w-10 object-contain mix-blend-multiply" />
            <div className="leading-none">
              <div className="font-serif text-2xl tracking-tight">Flowtastics</div>
              <div className="text-[10px] tracking-editorial uppercase text-charcoal/60 mt-1">A Wellness Community</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `text-[12px] tracking-editorial uppercase font-light transition-colors ${
                    isActive ? "text-teal" : "text-charcoal/70 hover:text-charcoal"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="text-charcoal/70 hover:text-teal transition-colors" data-testid="nav-instagram">
              <InstagramLogo size={20} weight="light" />
            </a>
            <a
              href="https://www.momoyoga.com/flowtastics/teacher-profile"
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-charcoal text-cream px-5 py-2.5 text-[11px] tracking-editorial uppercase hover:bg-teal transition-all duration-300"
              data-testid="nav-cta-book"
            >
              Book a Flow
            </a>
          </div>

          <button onClick={() => setOpen(true)} className="lg:hidden text-charcoal" data-testid="mobile-menu-open">
            <List size={26} weight="light" />
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-cream lg:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20">
              <div className="font-serif text-2xl">Flowtastics</div>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X size={26} weight="light" /></button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center gap-8">
              {NAV.map((n, i) => (
                <motion.div key={n.to}
                  initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <Link to={n.to} className="font-serif text-4xl tracking-tight" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="mt-4 text-charcoal/70" data-testid="mobile-instagram">
                <InstagramLogo size={28} weight="light" />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-charcoal text-cream/90 mt-32" data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO} alt="" className="h-12 w-12 object-contain bg-cream/10 rounded-full p-1" />
              <div>
                <div className="font-serif text-3xl">Flowtastics</div>
                <div className="text-[10px] tracking-editorial uppercase text-cream/50 mt-1">A Wellness Community</div>
              </div>
            </div>
            <p className="font-serif text-2xl leading-snug max-w-md text-cream/85 italic">
              "This is more than yoga — it's about weaving art, community, and empowerment into every flow."
            </p>
            <p className="text-xs uppercase tracking-editorial text-cream/45 mt-4">— Sophia Vito, Founder</p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-editorial text-cream/45 mb-5">Explore</div>
            <ul className="space-y-3 font-light">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-blush transition-colors" data-testid={`footer-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-editorial text-cream/45 mb-5">Connect</div>
            <ul className="space-y-3 font-light">
              <li><a href={`mailto:${CONTACT.email}`} className="hover:text-blush transition-colors break-all" data-testid="footer-email">{CONTACT.email}</a></li>
              <li><a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, "")}`} className="hover:text-blush transition-colors" data-testid="footer-phone">{CONTACT.phone}</a></li>
              <li><a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="hover:text-blush transition-colors inline-flex items-center gap-2" data-testid="footer-instagram"><InstagramLogo size={16} /> {CONTACT.instagramHandle}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-cream/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-editorial text-cream/40">
            <div>© {new Date().getFullYear()} Flowtastics. All flows reserved.</div>
            <div className="font-serif italic text-sm normal-case tracking-normal text-cream/60">Movement with meaning.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
